/**
 * @file planTips.ts
 * @brief Every (i) tooltip text on the plan screens, in one place: the "Your
 *        plan" card, the Compare plans table and the plan-change dialog.
 *        Web and the Mac app (DES 21-10-2026).
 *
 * @details
 * Edit the words here. Numbers (minutes per seat, fair-use hours, Free's
 * history days, minimum seats) are read from the catalogue rows passed in,
 * never written down; a tip whose number is missing drops that sentence.
 * One short plain sentence each, no em dashes.
 *
 * Pure (relative imports only) so node:test can load it.
 *
 * @author DES
 * @date 21-10-2026
 */

type Num = number | null | undefined;

/** The catalogue fields the tips read (a subset of a catalogue / compare row). */
export type TipPlan = {
  code: string;
  includedMinutes?: Num;
  notesMinutes?: Num;
  notesFairUseMinutes?: Num;
  historyDays?: Num;
  askLimit?: Num;
  minSeats?: Num;
  perSeat?: boolean | null;
  pooled?: boolean | null;
  enterprise?: boolean | null;
  contactSales?: boolean | null;
  /** B43: Free's minutes are one pool, never charged (W34 tips). */
  onePool?: boolean | null;
};

export type TipKey =
  | "price"
  | "billing"
  | "seats"
  | "nextBill"
  | "ends"
  | "notes"
  | "notesPlan"
  | "intel"
  | "pooled"
  | "ask"
  | "history"
  | "interpretation"
  | "extra"
  | "enterprise"
  | "mcp"
  | "taskApps"
  | "share";

const n = (v: Num) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const fmt = (v: number) => v.toLocaleString("en-GB");

/**
 * All tip texts, with numbers from `plans` (the catalogue, ideally `compare`
 * so the Free row is in it).
 */
export function planTips(plans: readonly TipPlan[]): Record<TipKey, string> {
  const free = plans.find((p) => p.code === "free");
  const pooled = plans.find((p) => p.pooled && n(p.includedMinutes));
  const perSeat = plans.find((p) => p.perSeat);
  const fairUse = plans.map((p) => n(p.notesFairUseMinutes)).find((v) => v != null) ?? null;
  const seatMin = n(perSeat?.minSeats);
  const poolMin = n(pooled?.includedMinutes);
  const freeDays = n(free?.historyDays);
  const freeNotes = n(free?.notesMinutes);
  const freeAsk = n(free?.askLimit);

  return {
    price: "What the plan costs for each month or year you are billed.",
    billing: "How often you are charged: once a month or once a year.",
    seats: `Every active member of your company is one seat${seatMin && seatMin > 1 ? `, and you pay for at least ${seatMin}` : ""}.`,
    nextBill: "The date and amount of your next charge. Any credit on your account is used first.",
    ends: "Your plan stays on until this date, then nothing more is charged.",
    notes: [
      fairUse != null
        ? `Notes for every meeting, under fair use of ${fmt(Math.round(fairUse / 60))} hours a month.`
        : "Notes for every meeting you record.",
      free?.onePool
        ? "On Free, Meeting Notes use the free minutes."
        : freeNotes != null
          ? `On Free, up to ${fmt(freeNotes)} minutes a month.`
          : "",
    ]
      .filter(Boolean)
      .join(" "),
    // The same without the Free sentence, for a paid plan's line (21-10-2026).
    notesPlan:
      fairUse != null
        ? `Notes for every meeting, under fair use of ${fmt(Math.round(fairUse / 60))} hours a month.`
        : "Notes for every meeting you record.",
    intel: "Minutes of full Meeting Intelligence: speakers, decisions, to-dos, key points and Ask AI.",
    pooled:
      poolMin != null
        ? `Every seat adds ${fmt(poolMin)} Intelligence minutes to one team pool. Anyone on the team can use them, so a busy member can use more than ${fmt(poolMin)}.`
        : "Every seat adds its Intelligence minutes to one team pool that anyone on the team can use.",
    ask: `Ask AI questions you can ask about your meetings each month.${freeAsk != null ? ` Free includes ${fmt(freeAsk)}.` : ""}`,
    history: `How long your meetings stay visible.${freeDays != null ? ` On Free, meetings older than ${fmt(freeDays)} days are hidden.` : ""}`,
    interpretation: "Live interpretation is charged for each minute of the session.",
    extra: "Charged per minute after the included minutes run out.",
    enterprise: "A plan priced for your company, with terms agreed with our team.",
    mcp: "Let Claude, ChatGPT and other AI assistants read your meetings, notes and to-dos with your permission.",
    taskApps: "Send meeting to-dos to the task apps your team uses, starting with Asana, with the moment each was agreed.",
    share: "Share a whole meeting, with its intelligence, speakers, to-dos and notes, with any VERBLIZR user. Included on every plan, Free too.",
  };
}

/** The (i) beside a plan's price in the Compare plans head. */
export function priceTip(plan: TipPlan): string {
  if (plan.enterprise || plan.contactSales) return planTips([plan]).enterprise;
  if (plan.code === "free") {
    // W34: since B43 a Free recording is never charged; interpretation after its free minutes is.
    return plan.onePool
      ? "Free costs nothing and recordings are never charged. Only interpretation after its free minutes is paid per minute."
      : "Free costs nothing. Full Intelligence and interpretation are paid per minute.";
  }
  if (plan.perSeat) {
    const min = n(plan.minSeats);
    return `The price for each seat.${min && min > 1 ? ` You pay for at least ${min} seats.` : ""}`;
  }
  return plan.code === "pro" ? "The price for one person." : planTips([plan]).price;
}

/** Compare plans row label → its tip (labels from planCompare.buildCompareRows). */
export const ROW_TIP: Record<string, TipKey> = {
  Seats: "seats",
  "Meeting Notes": "notes",
  "Intelligence minutes": "intel",
  "Shared by the team": "pooled",
  "Ask AI": "ask",
  History: "history",
  "Live interpretation": "interpretation",
  "Extra Intelligence": "extra",
  "Connect to AI assistants (MCP)": "mcp",
  "Send to-dos to your task apps": "taskApps",
  "Share meetings with VERBLIZR users": "share",
};

/**
 * Feature rows under the catalogue rows (DES 21-10-2026). Not catalogue
 * fields: who can really use each one today, checked in the code. A cell is
 * "Yes", "No" or a short condition. Free / Pro are personal accounts;
 * Business / Enterprise are companies.
 * - MCP: personal accounts always (BACKEND featureAccess
 *   isAiConnectorsAllowed: no company = allowed; web Profile → Connected AI
 *   apps). Company admins and members only once an admin turns on AI
 *   connectors in Company → Features (opt-in, off by default). A company
 *   admin's Free column is a company on Free, so it says the same as
 *   Business (`companyFree`, DES 21-10-2026).
 * - Task apps (Asana today, more to come; DES 21-10-2026: keep the words generic): every plan. Company admins connect in Company → Settings →
 *   Asana; personal accounts in Profile → Connected apps → Asana
 *   (/app/integrations/asana, linked 21-10-2026). The API takes any user.
 * - Sharing: every plan. BACKEND POST /boardroom/sessions/:id/shares checks
 *   only that you own the meeting and the person has an account (no plan
 *   check); web shows Share on your own meetings on /app and /company. Off
 *   only if BOARDROOM_SHARE_AFTER=false or an admin turns Boardroom off.
 */
export const FEATURE_ROWS: { label: string; tip: TipKey; cells: Record<string, string>; companyFree?: string }[] = [
  {
    label: "Connect to AI assistants (MCP)",
    tip: "mcp",
    cells: { free: "Yes", pro: "Yes", business: "Yes, once an admin turns it on", enterprise: "Yes, once an admin turns it on" },
    companyFree: "Yes, once an admin turns it on",
  },
  {
    label: "Send to-dos to your task apps",
    tip: "taskApps",
    cells: { free: "Yes", pro: "Yes", business: "Yes", enterprise: "Yes" },
  },
  {
    label: "Share meetings with VERBLIZR users",
    tip: "share",
    cells: { free: "Yes", pro: "Yes", business: "Yes", enterprise: "Yes" },
  },
];

/**
 * The feature rows for these columns, in the table's row shape. A company
 * admin's Free column reads as a company (`companyFree`).
 */
export function featureRows(
  plans: readonly { code: string }[],
  audience: "personal" | "company_admin" = "personal",
): { label: string; cells: string[] }[] {
  return FEATURE_ROWS.map((r) => ({
    label: r.label,
    cells: plans.map((p) =>
      audience === "company_admin" && p.code === "free" && r.companyFree ? r.companyFree : (r.cells[p.code] ?? "No"),
    ),
  }));
}
